import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { API } from '@/App';

const AuthContext = createContext(null);

// Emergent Auth OAuth URL
const EMERGENT_AUTH_URL = 'https://demobackend.emergentagent.com/auth/v1/env/oauth/google';
const CALLBACK_URL = `${window.location.origin}/auth/callback`;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impersonating, setImpersonating] = useState(null);
  // Tenant feature flags (Platform Admin gates nav/pages per tenant).
  // Defaults mirror the backend's DEFAULT_FEATURE_FLAGS so unauthenticated
  // and loading states don't flash optional nav items.
  const [featureFlags, setFeatureFlags] = useState({
    gap_library: false,
    gap_best_practices: false,
    cases: false,
    validation_codes: false,
    profanity_filter: false,
    feedback_alerts: false,
  });

  const fetchFeatureFlags = useCallback(async () => {
    try {
      const res = await fetch(`${API}/tenants/me/features`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        if (data?.feature_flags) setFeatureFlags(data.feature_flags);
      }
    } catch { /* keep defaults */ }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const response = await fetch(`${API}/auth/me`, {
        credentials: 'include'
      });
      if (response.ok) {
        // Read the response body exactly once via .text() + JSON.parse so
        // any future code path can't accidentally double-consume it and
        // trigger "body stream already read".
        const raw = await response.text();
        let data = {};
        if (raw) { try { data = JSON.parse(raw); } catch { data = {}; } }
        const userData = data.user || data;
        setUser(userData);

        // Always refresh feature flags so a tenant admin's nav reflects the
        // platform admin's most recent toggle without requiring a re-login.
        fetchFeatureFlags();

        // Check impersonation status for platform admins
        if (userData.role === 'platform_admin') {
          try {
            const impRes = await fetch(`${API}/platform/impersonation-status`, { credentials: 'include' });
            if (impRes.ok) {
              const impData = await impRes.json();
              setImpersonating(impData.impersonating ? impData : null);
            }
          } catch { /* not critical */ }
        } else {
          setImpersonating(null);
        }
      } else {
        setUser(null);
        setImpersonating(null);
      }
    } catch {
      setUser(null);
      setImpersonating(null);
    } finally {
      setLoading(false);
    }
  }, [fetchFeatureFlags]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const setUserData = useCallback((userData) => {
    setUser(userData);
  }, []);

  const exitImpersonation = useCallback(async () => {
    try {
      await fetch(`${API}/platform/exit-impersonate`, { method: 'POST', credentials: 'include' });
      setImpersonating(null);
      await checkAuth();
      window.location.href = '/platform';
    } catch (e) { console.error('Exit impersonation failed:', e); }
  }, [checkAuth]);

  // Google OAuth login - redirect to Emergent Auth OAuth
  const login = () => {
    window.location.href = `${EMERGENT_AUTH_URL}?callback_url=${encodeURIComponent(CALLBACK_URL)}`;
  };

  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (e) { console.error('Logout request failed:', e); }
    setUser(null);
    window.location.href = '/';
  };

  const dismissWelcome = useCallback(async () => {
    try {
      await fetch(`${API}/users/me/dismiss-welcome`, { method: 'POST', credentials: 'include' });
    } catch (e) { console.error('Dismiss welcome failed:', e); }
    setUser(prev => prev ? { ...prev, is_first_login: false } : prev);
  }, []);

  // ─── Role-based helper flags (single source of truth for UI gating) ─────────
  const role = user?.role || '';
  const isPlatformAdmin = role === 'platform_admin';
  const isTenantAdmin = role === 'tenant_admin';
  const isAreaManager = role === 'area_manager' || role === 'regional_manager';
  const isBranchManager = role === 'branch_manager';
  const isHQStaff = role === 'hq_staff';
  const canManage = isPlatformAdmin || isTenantAdmin;
  const canExport = !!role && !isHQStaff;

  // Feature-flag helper booleans (backed by GET /api/tenants/me/features).
  const hasGapLibrary = !!featureFlags.gap_library;
  const hasGapBestPractices = !!featureFlags.gap_best_practices;
  const hasCases = !!featureFlags.cases;
  const hasValidationCodes = !!featureFlags.validation_codes;
  const hasProfanityFilter = !!featureFlags.profanity_filter;
  const hasFeedbackAlerts = !!featureFlags.feedback_alerts;

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      logout,
      checkAuth,
      setUserData,
      impersonating,
      exitImpersonation,
      dismissWelcome,
      isAuthenticated: !!user,
      // Role helpers
      role,
      isPlatformAdmin,
      isTenantAdmin,
      isAreaManager,
      isBranchManager,
      isHQStaff,
      canManage,
      canExport,
      // Feature flags
      featureFlags,
      hasGapLibrary,
      hasGapBestPractices,
      hasCases,
      hasValidationCodes,
      hasProfanityFilter,
      hasFeedbackAlerts,
      refreshFeatureFlags: fetchFeatureFlags,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
